import { Box, CircularProgress, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { BORDER_RADIUS, COLORS } from "app/theme";
import { useMemo } from "react";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { MilkQualityTrendData } from "../hooks/useInsightsDashboardQueries";

const Card = styled(Box)({
  backgroundColor: "white",
  borderRadius: BORDER_RADIUS.medium,
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
  padding: 20,
});

type ChartRow = {
  period: string;
  fat: number | null;
  protein: number | null;
  volume: number | null;
};

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-GB", { month: "short", year: "2-digit" });
}

interface Props {
  data: MilkQualityTrendData[] | undefined;
  isLoading: boolean;
}

export default function MilkQualityTrendChart({ data, isLoading }: Props) {
  const chartData = useMemo<ChartRow[]>(() => {
    if (!data) return [];
    return data
      .map((r) => ({
        period: String(r["MilkQuality.periodStart"]).split("T")[0],
        fat:
          r["MilkQuality.avgFatPercent"] != null
            ? Number(r["MilkQuality.avgFatPercent"])
            : null,
        protein:
          r["MilkQuality.avgProteinPercent"] != null
            ? Number(r["MilkQuality.avgProteinPercent"])
            : null,
        volume:
          r["MilkQuality.avgVolumeLiters"] != null
            ? Number(r["MilkQuality.avgVolumeLiters"])
            : null,
      }))
      .sort((a, b) => a.period.localeCompare(b.period));
  }, [data]);

  if (isLoading) {
    return (
      <Card sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress size={24} />
      </Card>
    );
  }

  if (chartData.length === 0) {
    return (
      <Card>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
          Quality &amp; Volume Trends
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No data available
        </Typography>
      </Card>
    );
  }

  return (
    <Card>
      <Typography
        variant="subtitle2"
        sx={{ fontWeight: 600, color: "#18181b", mb: 0.5 }}
      >
        Quality &amp; Volume Trends
      </Typography>
      <Typography
        variant="caption"
        sx={{ color: "#71717a", mb: 1.5, display: "block" }}
      >
        Programme averages over time
      </Typography>
      <Box sx={{ width: "100%", height: 280 }}>
        <ResponsiveContainer>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              dataKey="period"
              tickFormatter={formatDate}
              tick={{ fontSize: 11, fill: "#71717a" }}
              tickLine={false}
            />
            <YAxis
              yAxisId="pct"
              tick={{ fontSize: 11, fill: "#71717a" }}
              tickLine={false}
              axisLine={false}
              domain={[0, "auto"]}
              label={{
                value: "%",
                angle: -90,
                position: "insideLeft",
                style: { fontSize: 11, fill: "#71717a" },
              }}
            />
            <YAxis
              yAxisId="vol"
              orientation="right"
              tick={{ fontSize: 11, fill: "#71717a" }}
              tickLine={false}
              axisLine={false}
              label={{
                value: "litres",
                angle: 90,
                position: "insideRight",
                style: { fontSize: 11, fill: "#71717a" },
              }}
            />
            <Tooltip
              labelFormatter={(label) => {
                const d = new Date(String(label));
                return d.toLocaleDateString("en-GB", {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                });
              }}
              formatter={(value: number, name: string) => {
                if (name === "volume")
                  return [
                    value.toLocaleString(undefined, {
                      maximumFractionDigits: 0,
                    }) + " L",
                    "Avg Volume",
                  ];
                const label = name === "fat" ? "Avg Fat" : "Avg Protein";
                return [
                  value.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }) + "%",
                  label,
                ];
              }}
            />
            <Legend
              formatter={(value: string) => {
                const labels: Record<string, string> = {
                  fat: "Fat %",
                  protein: "Protein %",
                  volume: "Volume (L)",
                };
                return labels[value] ?? value;
              }}
            />
            <Line
              yAxisId="pct"
              type="monotone"
              dataKey="fat"
              stroke={COLORS.dahlia[500]}
              strokeWidth={2}
              dot={false}
              connectNulls
            />
            <Line
              yAxisId="pct"
              type="monotone"
              dataKey="protein"
              stroke={COLORS.bellflower[500]}
              strokeWidth={2}
              dot={false}
              connectNulls
            />
            <Line
              yAxisId="vol"
              type="monotone"
              dataKey="volume"
              stroke={COLORS.daisy[500]}
              strokeWidth={2}
              dot={false}
              connectNulls
            />
          </LineChart>
        </ResponsiveContainer>
      </Box>
    </Card>
  );
}
